import React, { useCallback, useEffect, useState } from 'react';
import { ArrowRightLeft, Check, Loader2, Plus, Send, UserX, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { RaiseRequestDialog, StaffPickerDialog, TransferDialog, AgencyDialog } from '@/components/staffAttendance/ManpowerDialogs';
import { matchStaff, SearchNote } from '@/components/staffAttendance/StaffSearch';
import { CollarSelect } from '@/components/staffAttendance/SectionSelect';
import { CollarTiles } from '@/components/staffAttendance/CollarTiles';
import { staffAttApi, errMsg, todayIST, withPool } from '@/services/staffAttendanceApi';
import { staffMasterApi } from '@/services/staffMasterApi';

const STATE = { open: ['Open', 'bg-amber-100 text-amber-800'], partial: ['Partly filled', 'bg-sky-100 text-sky-800'], filled: ['Filled', 'bg-emerald-100 text-emerald-700'], closed: ['Closed', 'bg-gray-200 text-gray-600'] };
const DEP = { sent: 'Waiting acceptance', accepted: 'Accepted', not_arrived: 'Not arrived', cancelled: 'Cancelled' };

const DeploymentRow = ({ d, canReceive, onAct }) => (
  <div className="flex flex-wrap items-center gap-2 py-1.5 text-sm" data-testid={`mp-dep-${d.deployment_id}`}>
    <span className="flex-1 min-w-[160px]"><b>{d.staff_name}</b> <span className="text-gray-500">{d.gender === 'male' ? 'M' : d.gender === 'female' ? 'W' : ''}{d.collar ? ` · ${d.collar === 'white' ? 'White' : 'Blue'} collar` : ''} · from {d.from_section}{d.request_id ? '' : ` → ${d.to_section}`} · by {d.sent_by_name}</span></span>
    <span className="text-xs text-gray-600" data-testid={`mp-dep-status-${d.deployment_id}`}>{DEP[d.status]}</span>
    {d.status === 'sent' && canReceive && <>
      <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700" onClick={() => onAct(d, 'accept')} data-testid={`mp-dep-accept-${d.deployment_id}`}><Check className="w-3 h-3 mr-1" />Accept</Button>
      <Button size="sm" variant="outline" className="h-7" onClick={() => onAct(d, 'not-arrived')} data-testid={`mp-dep-notarrived-${d.deployment_id}`}><UserX className="w-3 h-3 mr-1" />Not arrived</Button>
    </>}
    {d.status === 'sent' && <Button size="sm" variant="ghost" className="h-7 text-red-600" onClick={() => onAct(d, 'cancel')} data-testid={`mp-dep-cancel-${d.deployment_id}`}><X className="w-3 h-3" /></Button>}
  </div>
);

const Num = ({ label, m, w, tone = 'text-gray-900', testId }) => (
  <div><div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div><div className={`font-bold tabular-nums ${tone}`} data-testid={testId}>{m}M · {w}W</div></div>
);

const RequestCard = ({ r, isHr, onSend, onAgency, onClose, onAct }) => {
  const [label, cls] = STATE[r.state];
  return (
    <Card className={`border-0 shadow-md ${r.state === 'closed' ? 'opacity-70' : ''}`} data-testid={`mp-req-${r.request_id}`}><CardContent className="p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="font-semibold text-lg">{r.section}</h4>
        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${cls}`} data-testid={`mp-req-state-${r.request_id}`}>{label}</span>
        <span className="text-xs text-gray-500">by {r.requested_by_name}{r.note ? ` — ${r.note}` : ''}</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Num label="Asked" m={r.men} w={r.women} testId={`mp-req-asked-${r.request_id}`} />
        <Num label="Accepted" m={r.accepted_men} w={r.accepted_women} tone="text-emerald-600" testId={`mp-req-accepted-${r.request_id}`} />
        <Num label="Waiting acceptance" m={r.awaiting_men} w={r.awaiting_women} tone="text-amber-600" testId={`mp-req-awaiting-${r.request_id}`} />
        <Num label="Agency" m={r.agency_men} w={r.agency_women} tone="text-violet-600" testId={`mp-req-agency-${r.request_id}`} />
        <Num label="Balance" m={r.balance_men} w={r.balance_women} tone="text-red-600" testId={`mp-req-balance-${r.request_id}`} />
      </div>
      {r.deployments.length > 0 && <div className="border-t pt-2 divide-y">{r.deployments.map((d) => <DeploymentRow key={d.deployment_id} d={d} canReceive={r.can_receive} onAct={onAct} />)}</div>}
      {r.state !== 'closed' && (
        <div className="flex flex-wrap gap-2">
          {(r.open_men + r.open_women > 0) && <Button size="sm" onClick={() => onSend(r)} className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid={`mp-req-send-${r.request_id}`}><Send className="w-3 h-3 mr-1" />Send staff</Button>}
          {isHr && r.balance_men + r.balance_women > 0 && <Button size="sm" variant="outline" onClick={() => onAgency(r)} data-testid={`mp-req-agency-btn-${r.request_id}`}>Agency supply</Button>}
          <Button size="sm" variant="ghost" onClick={() => onClose(r)} data-testid={`mp-req-close-${r.request_id}`}>Close request</Button>
        </div>
      )}
    </CardContent></Card>
  );
};

/** Hub tab: manpower requests (visible to all supervisors), day deployments, permanent transfer. */
export const ManpowerBoard = ({ search = '' }) => {
  const [date, setDate] = useState(todayIST());
  const [collar, setCollar] = useState('');
  const [data, setData] = useState(null);
  const [sections, setSections] = useState([]);
  const [dlg, setDlg] = useState({});
  const [transferId, setTransferId] = useState('');
  const [staff, setStaff] = useState([]);
  const load = useCallback(() => staffAttApi.manpower(date, collar).then(setData).catch((e) => toast.error(errMsg(e))), [date, collar]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    staffMasterApi.list({}).then((d) => {
      setStaff(d.staff.filter((s) => s.roll_status !== 'left'));
      setSections(withPool(d.staff.map((s) => s.section).filter(Boolean)).sort());
    }).catch(() => {});
  }, []);
  const done = () => { setDlg({}); load(); };
  const act = async (d, action) => {
    try { await staffAttApi.decideDeployment(d.deployment_id, action); toast.success('Updated'); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const closeReq = async (r) => {
    if (!window.confirm(`Close the ${r.section} request? The remaining balance is dropped.`)) return;
    try { await staffAttApi.closeRequest(r.request_id); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const tStaff = staff.find((s) => s.staff_id === transferId.split(' — ')[0]);
  const hit = (d) => matchStaff(search, d.staff_name, d.staff_id);
  const searching = !!search.trim();
  const requests = (data?.requests || []).map((r) => (searching ? { ...r, deployments: r.deployments.filter(hit) } : r)).filter((r) => !searching || r.deployments.length);
  const direct = (data?.direct || []).filter(hit);
  const allDeps = data ? data.requests.reduce((n, r) => n + r.deployments.length, data.direct.length) : 0;

  return (
    <div className="space-y-4" data-testid="manpower-board">
      <div className="flex flex-wrap items-center gap-2">
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value || todayIST())} className="w-[170px] bg-white" data-testid="mp-date" />
        <CollarSelect value={collar} onChange={setCollar} testId="mp-collar-select" />
        <Button onClick={() => setDlg({ raise: true })} className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="mp-raise-btn"><Plus className="w-4 h-4 mr-1" />Request manpower</Button>
        <Button variant="outline" onClick={() => setDlg({ pick: true })} data-testid="mp-deploy-btn"><Send className="w-4 h-4 mr-1" />Deploy staff for the day</Button>
        <div className="flex items-center gap-1">
          <Input value={transferId} onChange={(e) => setTransferId(e.target.value)} list="mp-transfer-list" placeholder="Staff to transfer permanently" className="w-[240px] bg-white" data-testid="mp-transfer-staff" />
          <datalist id="mp-transfer-list">{staff.map((s) => <option key={s.staff_id} value={`${s.staff_id} — ${s.staff_name}`} />)}</datalist>
          <Button variant="outline" disabled={!tStaff} onClick={() => setDlg({ transfer: tStaff })} data-testid="mp-transfer-btn"><ArrowRightLeft className="w-4 h-4 mr-1" />Transfer</Button>
        </div>
        {data && <span className="ml-auto rounded-lg bg-red-50 border border-red-200 px-3 py-1.5 text-sm" data-testid="mp-pending-balance">Pending for HR: <b>{data.pending_balance.men} men · {data.pending_balance.women} women</b></span>}
      </div>
      {!data ? <Loader2 className="w-6 h-6 animate-spin mx-auto text-amber-600" /> : (
        <>
          <CollarTiles split={data.collar_split} value={collar} onPick={setCollar} metric="Staff sent" testId="mp-collar-tiles" />
          {data.requests.length === 0 && <p className="text-center text-gray-400 py-6" data-testid="mp-no-requests">No manpower requests for {date}.</p>}
          <SearchNote q={search} shown={requests.reduce((n, r) => n + r.deployments.length, direct.length)} total={allDeps} testId="mp-search-note" />
          <div className="grid gap-4 lg:grid-cols-2">
            {requests.map((r) => <RequestCard key={r.request_id} r={r} isHr={data.is_hr} onAct={act} onClose={closeReq}
              onSend={(x) => setDlg({ pick: true, request: x })} onAgency={(x) => setDlg({ agency: x })} />)}
          </div>
          {direct.length > 0 && (
            <Card className="border-0 shadow-md" data-testid="mp-direct"><CardContent className="p-4">
              <h4 className="font-semibold mb-1">Day deployments (no request)</h4>
              <div className="divide-y">{direct.map((d) => <DeploymentRow key={d.deployment_id} d={d} canReceive={d.can_receive} onAct={act} />)}</div>
            </CardContent></Card>
          )}
        </>
      )}
      <RaiseRequestDialog open={!!dlg.raise} onClose={() => setDlg({})} onDone={done} sections={sections} date={date} />
      <StaffPickerDialog open={!!dlg.pick} onClose={() => setDlg({})} onDone={done} date={date} request={dlg.request} sections={sections} />
      <TransferDialog open={!!dlg.transfer} onClose={() => setDlg({})} onDone={() => { setTransferId(''); done(); }} staff={dlg.transfer} sections={sections} />
      <AgencyDialog open={!!dlg.agency} onClose={() => setDlg({})} onDone={done} request={dlg.agency} />
    </div>
  );
};

export default ManpowerBoard;
